import { usePage } from "@inertiajs/react"
import { CARD_FILM } from "@/lib/films"
import type { ArticleLink } from "@/components/ResidentTherapistCard"
import type { PageProps, GiselaTherapist } from "@/types/inertia"
import { BrandMotif } from "@/components/ui/brand-motif"

interface Props {
  therapist: GiselaTherapist | null
  /** The home hero's first photograph — this card's background, so the page's
   *  own picture carries through to the offer it opens with. */
  heroSlide?: { photo_url: string; alt_text?: string | null } | null
  /** Her three latest pieces — the blog cross-link, inside her card. */
  articles?: ArticleLink[]
  content: {
    /** The card's own portrait, placed in the admin; her profile photo when unset. */
    photo?: { id: number; url: string | null; card_url?: string | null; alt_text: string | null } | null
    clients_label: string
    clients_heading: string
    clients_body: string
    clients_cta: string
    therapists_label: string
    therapists_heading: string
    therapists_body: string
    therapists_cta: string
  }
}

// The venue home page's fork: therapy on the left, room hire on the right.
// Gisela's card lives in the therapy half — she is the only therapist the site
// lists, so a second "Our resident therapist" band below this one repeated her
// photo, name and speciality one screen apart. Her portrait, and the links into
// her blog, moved in here instead.
//
// The portrait is a photo slot on this section (Audience Cards → Therapy card
// — Portrait), so the venue's home can introduce her with a picture of its own
// choosing; her Profile photo is what shows until one is placed.
export default function HomeAudiences({ therapist, heroSlide, articles = [], content }: Props) {
  const { brand_urls, translations } = usePage<PageProps>().props
  const t = translations.home.audiences
  const tc = translations.sanctuary_pages.common

  // CMS copy wins, but a blank field falls back to the i18n default so the
  // cards can never render empty headings or label-less CTA pills.
  const field = (key: Exclude<keyof Props["content"], "photo">) => content[key] || (t[key] as string)

  // The placed photo wins; her profile photo stands in behind it.
  const portrait = content.photo?.card_url || content.photo?.url || therapist?.photo_url || null
  const portraitAlt = content.photo ? content.photo.alt_text ?? "" : therapist?.name ?? ""

  return (
    <section className="relative overflow-hidden bg-cream py-20">
      <BrandMotif variant="rings" size={700} className="absolute -top-64 -right-72" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Left: therapy audience — the primary offer, visually dominant.
              Both cards are columns ending in their CTA, and both CTAs are
              pushed to the bottom, so the two buttons sit on one line however
              much copy either card carries. */}
          <div className="relative flex overflow-hidden rounded-2xl bg-teal text-white">
            {/* The home hero's photograph under the deep-blue film — the same
                picture the page opens with, so the card reads as part of it
                rather than a flat blue panel. The film is fixed rather than
                the hero's own setting: this copy is denser than a hero's and
                needs the deeper wash whatever the hero is wearing. */}
            {heroSlide && (
              <div className="absolute inset-0" aria-hidden="true">
                <img
                  src={heroSlide.photo_url}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0" style={{ background: CARD_FILM }} />
              </div>
            )}
            <div className="relative flex h-full w-full flex-col p-8 lg:p-10">
              <span className="text-xs font-medium uppercase tracking-widest text-white/80">
                {field("clients_label")}
              </span>
              <h2 className="mt-3 text-white">{field("clients_heading")}</h2>
              <p className="mt-4 text-white">
                {field("clients_body")}
              </p>

              {therapist && (
                <div className="mt-7 flex flex-col gap-5 sm:flex-row sm:items-center">
                  {/* Her portrait as her own hero draws it: a disc with the same
                      circle offset up and left behind it, so a crescent of line
                      shows past the picture. It is the venue's introduction to
                      her, and it should look like the page it introduces. */}
                  <div className="relative h-40 w-40 shrink-0">
                    <span
                      aria-hidden
                      className="absolute -top-[3.4%] -left-[4.5%] h-full w-full rounded-full border-2 border-white/55"
                    />
                    {portrait ? (
                      <img
                        src={portrait}
                        alt={portraitAlt}
                        className="relative h-full w-full rounded-full object-cover shadow-lg"
                      />
                    ) : (
                      <div className="relative flex h-full w-full items-center justify-center rounded-full bg-white/10">
                        <span className="font-display text-3xl text-white/80">GR</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-white">{therapist.name}</p>
                    <p className="text-sm text-white">
                      {therapist.speciality_en ?? tc.resident_speciality_fallback}
                      {tc.resident_online_note}
                    </p>
                  </div>
                </div>
              )}

              {/* Titles only — article bodies never render on the venue site. */}
              {articles.length > 0 && (
                <div className="mt-8 border-t border-white/15 pt-5">
                  <p className="mb-2 text-xs font-medium uppercase tracking-widest text-white/80">
                    {tc.from_resident_therapist}
                  </p>
                  <ul className="m-0 list-none space-y-1.5 p-0">
                    {articles.map((a) => (
                      <li key={a.slug}>
                        <a
                          href={`${brand_urls.gisela}/blog/${a.slug}`}
                          className="text-sm text-white underline-offset-4 hover:underline"
                        >
                          {a.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-auto pt-8">
                <a
                  href="/therapy-with-gisela"
                  data-audience-cta="therapy"
                  className="inline-block rounded-full bg-white px-6 py-2.5 text-sm font-medium text-teal shadow transition-opacity hover:opacity-90"
                >
                  {field("clients_cta")}
                </a>
              </div>
            </div>
          </div>

          {/* Right: room hire audience — kept, visually secondary */}
          <div className="flex flex-col rounded-2xl bg-white p-8 ring-1 ring-hairline lg:p-10">
            <span className="text-xs font-medium uppercase tracking-widest text-accent">
              {field("therapists_label")}
            </span>
            <h2 className="mt-3">{field("therapists_heading")}</h2>
            <p className="mt-4 text-ink-muted">
              {field("therapists_body")}
            </p>

            <ul className="mt-6 space-y-2">
              {(t.features as string[]).map((feature: string) => (
                <li key={feature} className="flex items-center gap-2 text-sm text-ink-body">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-sage" />
                  {feature}
                </li>
              ))}
            </ul>

            {/* Filled, not outlined: it is the one thing to do on a white
                card, and the pair of cards now reads as one offer each rather
                than a primary and an afterthought. */}
            <div className="mt-auto pt-8">
              <a
                href="/rent-a-room"
                data-audience-cta="rooms"
                className="inline-block rounded-full bg-teal px-6 py-2.5 text-sm font-medium text-white no-underline shadow transition-opacity hover:opacity-90"
              >
                {field("therapists_cta")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
